import { OfferType, RankType } from '@smg-real-estate/hg-rets-schema';

import {
  CompletenessLevel,
  InsertionSource,
  PerformanceLevel,
  type SearchFilterData,
} from '@/models/insertionsParameters';
import { InsertionStatus } from '@/types';

const STATUS_ALLOW = new Set(Object.values(InsertionStatus));
const OFFER_TYPES_ALLOW = new Set(Object.values(OfferType));
const RANK_TYPES_ALLOW = new Set(Object.values(RankType));
const SOURCE_ALLOW = new Set(Object.values(InsertionSource));
const COMPLETENESS_ALLOW = new Set(Object.values(CompletenessLevel));
const PERFORMANCE_ALLOW = new Set(Object.values(PerformanceLevel));

/**
 * Search in the object whether key exists.
 * Alternatively, the default value or an empty array is returned.
 *
 * @param key - key
 * @param queryParams - queryParams
 * @param searchFilterDefaults - searchFilterDefaults
 */
const getQueryParam = (
  key: string,
  queryParams: SearchFilterData,
  searchFilterDefaults: SearchFilterData,
): any[] => {
  const params: any = key in queryParams ? queryParams[key] : null;
  return params || searchFilterDefaults[key] || [];
};

/**
 * Boolean counterpart of `getQueryParam`. It cannot reuse that helper because a stored `false`
 * is falsy, so the `||` chain there would fall through to the default and then to `[]`.
 * Only a real boolean `true` enables the filter; anything else (missing, string, number) is off.
 *
 * "Off" is `undefined` rather than `false` on purpose. `AdListFilter` rewrites the route whenever
 * `JSON.stringify(searchFilter)` differs from the `searchFilter` query param, so emitting a `false`
 * here would make every pre-existing deep link (which has no `regionalHighlight` key) mismatch and
 * fire a debounced `router.replace` ~800ms after load, clobbering any navigation the user started
 * in the meantime. `JSON.stringify` drops undefined values, so an untouched link stays byte-identical.
 */
const getBooleanQueryParam = (
  key: string,
  queryParams: SearchFilterData,
  searchFilterDefaults: SearchFilterData,
): true | undefined => {
  const value = key in queryParams ? queryParams[key] : searchFilterDefaults[key];
  return value === true ? true : undefined;
};

/**
 * This function searches in the URLSearchParams for "searchFilter" (string in JSON format).
 * The values are partially compared with allowed values.
 * In case of illegal or incorrect values the predefined default value is returned.
 */
export default function searchFilterQueryDataValidation(
  searchFilterDefaults: SearchFilterData,
): SearchFilterData {
  const searchParams = new URLSearchParams(window.location.search);
  const searchFilter = searchParams.get('searchFilter');

  let queryParams = {};
  try {
    queryParams = searchFilter ? JSON.parse(searchFilter) : {};
  } catch (_) {
    // Silence error
  }

  return {
    category: getQueryParam('category', queryParams, searchFilterDefaults),
    searchQuery: getQueryParam('searchQuery', queryParams, searchFilterDefaults),
    status: getQueryParam('status', queryParams, searchFilterDefaults).filter((item) =>
      STATUS_ALLOW.has(item),
    ),
    offerType: getQueryParam('offerType', queryParams, searchFilterDefaults).filter((item) =>
      OFFER_TYPES_ALLOW.has(item),
    ),
    rankType: getQueryParam('rankType', queryParams, searchFilterDefaults).filter((item) =>
      RANK_TYPES_ALLOW.has(item),
    ),
    regionalHighlight: getBooleanQueryParam('regionalHighlight', queryParams, searchFilterDefaults),
    source: getQueryParam('source', queryParams, searchFilterDefaults).filter((item) =>
      SOURCE_ALLOW.has(item),
    ),
    completeness: getQueryParam('completeness', queryParams, searchFilterDefaults).filter((item) =>
      COMPLETENESS_ALLOW.has(item),
    ),
    performance: getQueryParam('performance', queryParams, searchFilterDefaults).filter((item) =>
      PERFORMANCE_ALLOW.has(item),
    ),
  };
}
